import { KonvaEventObject } from 'konva/lib/Node';
import { useCallback, useEffect, useRef, useState } from 'react';

interface CanvasPosition {
  x: number;
  y: number;
}

interface CanvasDimensions {
  width: number;
  height: number;
}

interface HookApi {
  onMouseWheel: (e: KonvaEventObject<WheelEvent>) => void;
  onStageDragMove: (e: KonvaEventObject<MouseEvent>) => void;
  onStageDragEnd: (e: KonvaEventObject<MouseEvent>) => void;
  canvasPosition: CanvasPosition;
  canvasScale: number;
  setView: (view: { position: CanvasPosition; scale: number }) => void;
}

export const SCALE_LIMIT = { min: 0.1, max: 4 };
const SCALE_SPEED = 1.1;

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

export const useCanvasScale = (): HookApi => {
  const [canvasPosition, setCanvasPosition] = useState<CanvasPosition>({ x: 0, y: 0 });
  const [canvasScale, setCanvasScale] = useState(1);
  const dragFrameRef = useRef<number | null>(null);
  const pendingDragPositionRef = useRef<CanvasPosition | null>(null);

  const commitPendingDragPosition = useCallback(() => {
    dragFrameRef.current = null;
    const nextPosition = pendingDragPositionRef.current;
    if (!nextPosition) return;

    pendingDragPositionRef.current = null;
    setCanvasPosition((prevPosition) =>
      prevPosition.x === nextPosition.x && prevPosition.y === nextPosition.y ? prevPosition : nextPosition,
    );
  }, []);

  useEffect(() => {
    return () => {
      if (dragFrameRef.current !== null) {
        cancelAnimationFrame(dragFrameRef.current);
      }
    };
  }, []);

  const onMouseWheel = useCallback((e: KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();

    const stage = e.target.getStage();

    if (!stage) return;

    const oldScale = stage.scaleX();
    const cursorPosition = stage.getPointerPosition();

    if (!cursorPosition) return;

    const mousePointTo = {
      x: (cursorPosition.x - stage.x()) / oldScale,
      y: (cursorPosition.y - stage.y()) / oldScale,
    };

    // Normalise wheel delta so trackpad streams (many small events) and mouse
    // wheels (single ~100 events) feel the same. |normalized| ∈ [0, 1].
    const rawDelta = e.evt.deltaY;
    const normalized = Math.sign(rawDelta) * Math.min(Math.abs(rawDelta) / 100, 1);
    const factor = 1 - normalized * (SCALE_SPEED - 1);
    const finalScale = clamp(oldScale * factor, SCALE_LIMIT.min, SCALE_LIMIT.max);

    setCanvasScale(finalScale);
    setCanvasPosition({
      x: cursorPosition.x - mousePointTo.x * finalScale,
      y: cursorPosition.y - mousePointTo.y * finalScale,
    });
  }, []);

  const onStageDragMove = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      const stage = e.target.getStage();

      if (!stage || e.target !== stage) {
        return;
      }

      pendingDragPositionRef.current = { x: stage.x(), y: stage.y() };
      if (dragFrameRef.current === null) {
        dragFrameRef.current = requestAnimationFrame(commitPendingDragPosition);
      }
    },
    [commitPendingDragPosition],
  );

  const onStageDragEnd = useCallback((e: KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();

    if (!stage || e.target !== stage) {
      return;
    }

    if (dragFrameRef.current !== null) {
      cancelAnimationFrame(dragFrameRef.current);
      dragFrameRef.current = null;
    }

    pendingDragPositionRef.current = null;
    const nextPosition = { x: stage.x(), y: stage.y() };
    setCanvasPosition((prevPosition) =>
      prevPosition.x === nextPosition.x && prevPosition.y === nextPosition.y ? prevPosition : nextPosition,
    );
  }, []);

  const setView = useCallback(({ position, scale }: { position: CanvasPosition; scale: number }) => {
    setCanvasPosition(position);
    setCanvasScale(clamp(scale, SCALE_LIMIT.min, SCALE_LIMIT.max));
  }, []);

  return {
    canvasPosition,
    canvasScale,
    onMouseWheel,
    onStageDragMove,
    onStageDragEnd,
    setView,
  };
};

export const computeFitView = (
  bounds: { minX: number; minY: number; width: number; height: number } | null,
  dimensions: CanvasDimensions,
  padding = 40,
): { position: CanvasPosition; scale: number } => {
  if (!bounds || !dimensions.width || !dimensions.height || bounds.width <= 0 || bounds.height <= 0) {
    return { position: { x: 0, y: 0 }, scale: 1 };
  }

  const scaleX = (dimensions.width - padding * 2) / bounds.width;
  const scaleY = (dimensions.height - padding * 2) / bounds.height;
  const scale = clamp(Math.min(scaleX, scaleY), SCALE_LIMIT.min, SCALE_LIMIT.max);

  const contentCenterX = bounds.minX + bounds.width / 2;
  const contentCenterY = bounds.minY + bounds.height / 2;

  return {
    position: {
      x: dimensions.width / 2 - contentCenterX * scale,
      y: dimensions.height / 2 - contentCenterY * scale,
    },
    scale,
  };
};
